import { create } from 'zustand';
import React, { useEffect } from 'react';

// Simple, stable Zustand store without complex middleware
const useStaffStore = create((set, get) => ({
  // State
  staff: [],
  rotaShifts: [],
  loading: false,
  error: null,

  // Actions
  createStaff: (staffData) => {
    const id = `staff_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
    const staff = {
      id,
      employeeId: staffData?.employeeId || '',
      firstName: staffData?.firstName || '',
      lastName: staffData?.lastName || '',
      email: staffData?.email || '',
      phone: staffData?.phone || '',
      centreId: staffData?.centreId,
      role: staffData?.role || 'practitioner',
      qualifications: staffData?.qualifications || [],
      dbsCheck: {
        status: staffData?.dbsStatus || 'pending',
        number: staffData?.dbsNumber || '',
        issueDate: staffData?.dbsIssueDate || '',
        expiryDate: staffData?.dbsExpiryDate || ''
      },
      emergencyContact: staffData?.emergencyContact || {},
      startDate: staffData?.startDate || '',
      contractType: staffData?.contractType || 'permanent',
      hoursPerWeek: staffData?.hoursPerWeek || 40,
      hourlyRate: staffData?.hourlyRate || 0,
      isActive: true,
      createdAt: new Date()?.toISOString(),
      updatedAt: new Date()?.toISOString()
    };
    
    set(state => ({
      staff: [...state?.staff, staff]
    }));
  },

  updateStaff: (id, updates) => {
    set(state => ({
      staff: state?.staff?.map(staff => 
        staff?.id === id 
          ? { ...staff, ...updates, updatedAt: new Date()?.toISOString() }
          : staff
      )
    }));
  },

  deleteStaff: (id) => {
    set(state => ({
      staff: state?.staff?.map(staff => 
        staff?.id === id 
          ? { ...staff, isActive: false, updatedAt: new Date()?.toISOString() }
          : staff
      )
    }));
  },

  // Helper functions
  getStaffByCentre: (centreId) => {
    const state = get();
    return state?.staff?.filter(staff => staff?.centreId === centreId && staff?.isActive) || [];
  },

  getStaffById: (id) => {
    const state = get();
    return state?.staff?.find(staff => staff?.id === id) || null;
  },

  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),

  // Enhanced demo data initialization
  initializeDemoData: () => {
    const state = get();
    
    // Only initialize if not already done
    if (state?.staff?.length > 0) return;

    const demoStaff = [
      {
        id: 'staff_1',
        employeeId: 'EMP001',
        firstName: 'Sarah',
        lastName: 'Thompson',
        email: 'sarah.thompson@littlestars.com',
        phone: '07123456789',
        centreId: 'demo_centre_1',
        role: 'room_leader',
        qualifications: [
          { type: 'level3', name: 'Level 3 Childcare', issueDate: '2020-06-15', expiryDate: '2025-06-15', includes: 'level3' },
          { type: 'first_aid', name: 'Paediatric First Aid', issueDate: '2023-01-10', expiryDate: '2026-01-10', includes: 'first_aid' }
        ],
        dbsCheck: { status: 'valid', number: 'DBS123456', issueDate: '2023-05-01', expiryDate: '2026-05-01' },
        startDate: '2020-09-01',
        contractType: 'permanent',
        hoursPerWeek: 40,
        hourlyRate: 12.50,
        isActive: true,
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      },
      {
        id: 'staff_2',
        employeeId: 'EMP002',
        firstName: 'Michael',
        lastName: 'Jones',
        email: 'michael.jones@littlestars.com',
        phone: '07987654321',
        centreId: 'demo_centre_1',
        role: 'practitioner',
        qualifications: [
          { type: 'level3', name: 'Level 3 Childcare', issueDate: '2019-03-20', expiryDate: '2024-03-20', includes: 'level3' },
          { type: 'safeguarding', name: 'Safeguarding Children', issueDate: '2023-02-15', expiryDate: '2026-02-15', includes: 'safeguarding' }
        ],
        dbsCheck: { status: 'valid', number: 'DBS789012', issueDate: '2023-04-15', expiryDate: '2026-04-15' },
        startDate: '2019-08-15',
        contractType: 'permanent',
        hoursPerWeek: 35,
        hourlyRate: 11.80,
        isActive: true,
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      },
      {
        id: 'staff_3',
        employeeId: 'EMP003',
        firstName: 'Lisa',
        lastName: 'Williams',
        email: 'lisa.williams@littlestars.com',
        phone: '07555123456',
        centreId: 'demo_centre_1',
        role: 'apprentice',
        qualifications: [
          { type: 'level2', name: 'Level 2 Childcare', issueDate: '2022-09-10', expiryDate: '2027-09-10', includes: 'level2' }
        ],
        dbsCheck: { status: 'valid', number: 'DBS345678', issueDate: '2023-03-01', expiryDate: '2026-03-01' },
        startDate: '2022-09-01',
        contractType: 'apprentice',
        hoursPerWeek: 30,
        hourlyRate: 9.50,
        isActive: true,
        createdAt: new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      }
    ];

    set(state => ({
      staff: demoStaff
    }));
  }
}));

export default useStaffStore;

// Simple hook that uses stable selectors
export const useStaff = () => {
  const staff = useStaffStore(state => state?.staff);
  const loading = useStaffStore(state => state?.loading);
  const error = useStaffStore(state => state?.error);
  const createStaff = useStaffStore(state => state?.createStaff);
  const updateStaff = useStaffStore(state => state?.updateStaff);
  const deleteStaff = useStaffStore(state => state?.deleteStaff);
  const getStaffByCentre = useStaffStore(state => state?.getStaffByCentre);
  const getStaffById = useStaffStore(state => state?.getStaffById);
  const setLoading = useStaffStore(state => state?.setLoading);
  const setError = useStaffStore(state => state?.setError);
  const initializeDemoData = useStaffStore(state => state?.initializeDemoData);

  // Initialize demo data on first load
  React.useEffect(() => {
    if (!staff || staff?.length === 0) {
      initializeDemoData?.();
    }
  }, [staff?.length, initializeDemoData]);

  return {
    staff,
    loading,
    error,
    createStaff,
    updateStaff,
    deleteStaff,
    getStaffByCentre,
    getStaffById,
    setLoading,
    setError,
    initializeDemoData
  };
};